/**
 * Tearsheets HTTP operations (contract-first — every PathDef here is wired in the BFF).
 *
 * FE owns tearsheet components/formatting; PDF export loads that SPA in Chromium and stores the artifact.
 * BFF stores layouts, skins, branding components, report drafts, and PDF links. SOAP: funds, live data.
 * Verb rule: PUT upserts resources; POST creates skins / report drafts; GET reads; DELETE deletes.
 */
import {
  FundList,
  LiveTearsheetQuery,
  TearsheetData,
  TearsheetIndexList,
  TearsheetIndexQuery,
  TearsheetLayout,
  TearsheetLayoutComponent,
  TearsheetLayoutComponentAssetInput,
  TearsheetLayoutComponentInput,
  TearsheetLayoutComponentList,
  TearsheetLayoutComponentListQuery,
  TearsheetLayoutFavoritesInput,
  TearsheetLayoutInput,
  TearsheetLayoutList,
  TearsheetReport,
  TearsheetReportInput,
  TearsheetReportList,
  TearsheetReportListQuery,
  TearsheetReportPdf,
  TearsheetReportPdfQuery,
  TearsheetReportUpdate,
  TearsheetSkinInput,
  TearsheetSkinList,
  TearsheetSkinPinsInput,
  TearsheetTermList,
  TearsheetTermQuery,
} from '../interfaces/tearsheets';
import { empty, json, problem, type PathDef } from '../../util/pathDef';

export const tearsheetsDataGet: PathDef = {
  method: 'get',
  path: '/tearsheets/data',
  handler: 'domains/bff/src/tearsheets/data/index.handler',
  tags: ['tearsheets'],
  summary: 'Get live tearsheet data for FE rendering',
  description: 'Returns HedgeFacts data for FE rendering. Frontend owns HTML/PDF generation; BFF does not render.',
  security: 'bearerAuth',
  query: LiveTearsheetQuery,
  responses: {
    200: json('Live tearsheet data', TearsheetData),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    500: problem('BFF assembled an invalid tearsheet'),
    502: problem('HedgeFacts API unavailable'),
  },
};

export const tearsheetsTermsGet: PathDef = {
  method: 'get',
  path: '/tearsheets/terms',
  handler: 'domains/bff/src/tearsheets/terms/index.handler',
  tags: ['tearsheets'],
  summary: 'List reporting term options',
  description: 'Fund monthly reporting dates from monthly return history.',
  security: 'bearerAuth',
  query: TearsheetTermQuery,
  responses: {
    200: json('Terms', TearsheetTermList),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    502: problem('HedgeFacts API unavailable'),
  },
};

export const fundsGet: PathDef = {
  method: 'get',
  path: '/funds',
  handler: 'domains/bff/src/funds/list/index.handler',
  tags: ['funds'],
  summary: 'List funds available to the user',
  description:
    'FACTSTransferWS getFundsList (ticket from CreateHedgeTopTicket). Entitlements filter lands in #14292; this slice returns all SOAP funds for the session.',
  security: 'bearerAuth',
  responses: {
    200: json('Funds', FundList),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    502: problem('HedgeFacts API unavailable'),
  },
};

export const tearsheetsLayoutsGet: PathDef = {
  method: 'get',
  path: '/tearsheets/layouts',
  handler: 'domains/bff/src/tearsheets/layouts/list/index.handler',
  tags: ['tearsheets'],
  summary: 'List layouts',
  description: 'Account-scoped (fund-agnostic). Returns stock layouts plus saved user layouts.',
  security: 'bearerAuth',
  responses: {
    200: json('Layouts', TearsheetLayoutList),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
  },
};

export const tearsheetsLayoutPut: PathDef = {
  method: 'put',
  path: '/tearsheets/layouts/{layoutId}',
  handler: 'domains/bff/src/tearsheets/layouts/put/index.handler',
  tags: ['tearsheets'],
  summary: 'Upsert layout',
  security: 'bearerAuth',
  request: TearsheetLayoutInput,
  responses: {
    200: json('Layout updated', TearsheetLayout),
    201: json('Layout created', TearsheetLayout),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    409: problem('Stock layouts cannot be overwritten'),
  },
};

export const tearsheetsLayoutsFavoritesPut: PathDef = {
  method: 'put',
  path: '/tearsheets/layouts/favorites',
  handler: 'domains/bff/src/tearsheets/layouts/favorites/index.handler',
  tags: ['tearsheets'],
  summary: 'Save layout favorites',
  description: 'Replaces the tenant-wide up-to-3 favorite layout ids shown on Create.',
  security: 'bearerAuth',
  request: TearsheetLayoutFavoritesInput,
  responses: {
    200: json('Layouts', TearsheetLayoutList),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
  },
};

export const tearsheetsLayoutDelete: PathDef = {
  method: 'delete',
  path: '/tearsheets/layouts/{layoutId}',
  handler: 'domains/bff/src/tearsheets/layouts/delete/index.handler',
  tags: ['tearsheets'],
  summary: 'Delete layout',
  security: 'bearerAuth',
  responses: {
    204: empty('Deleted'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    404: problem('Not found'),
    409: problem('Stock layout or layout in use by a non-draft report'),
  },
};

export const tearsheetsSkinsGet: PathDef = {
  method: 'get',
  path: '/tearsheets/skins',
  handler: 'domains/bff/src/tearsheets/skins/list/index.handler',
  tags: ['tearsheets'],
  summary: 'List skins',
  description: 'Global stock skins, the caller’s own skins, and public skins from the same datasource.',
  security: 'bearerAuth',
  responses: {
    200: json('Skins', TearsheetSkinList),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    502: problem('Skins service unavailable'),
  },
};

export const tearsheetsSkinsPost: PathDef = {
  method: 'post',
  path: '/tearsheets/skins',
  handler: 'domains/bff/src/tearsheets/skins/post/index.handler',
  tags: ['tearsheets'],
  summary: 'Create skin',
  description: 'Creates a new custom skin owned by the caller. Duplicate a stock or public skin by posting its colors.',
  security: 'bearerAuth',
  request: TearsheetSkinInput,
  responses: {
    201: json('Skin created', TearsheetSkinList),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    502: problem('Skins service unavailable'),
  },
};

export const tearsheetsSkinsOrderPut: PathDef = {
  method: 'put',
  path: '/tearsheets/skins/order',
  handler: 'domains/bff/src/tearsheets/skins/pins/index.handler',
  tags: ['tearsheets'],
  summary: 'Save skin order',
  description: 'Replaces the tenant-wide up-to-5 main-screen skin pin list.',
  security: 'bearerAuth',
  request: TearsheetSkinPinsInput,
  responses: {
    200: json('Skins', TearsheetSkinList),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    502: problem('Skins service unavailable'),
  },
};

export const tearsheetsSkinPut: PathDef = {
  method: 'put',
  path: '/tearsheets/skins/{skinId}',
  handler: 'domains/bff/src/tearsheets/skins/put/index.handler',
  tags: ['tearsheets'],
  summary: 'Edit skin',
  description: 'Updates a custom skin the caller owns, including the public/private flag. Stock and other people’s skins cannot be edited.',
  security: 'bearerAuth',
  request: TearsheetSkinInput,
  responses: {
    200: json('Skin updated', TearsheetSkinList),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    404: problem('Not found'),
    409: problem('Stock skins cannot be overwritten'),
    502: problem('Skins service unavailable'),
  },
};

export const tearsheetsSkinDelete: PathDef = {
  method: 'delete',
  path: '/tearsheets/skins/{skinId}',
  handler: 'domains/bff/src/tearsheets/skins/delete/index.handler',
  tags: ['tearsheets'],
  summary: 'Delete skin',
  security: 'bearerAuth',
  responses: {
    204: empty('Deleted'),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    404: problem('Not found'),
    409: problem('Stock skins cannot be deleted'),
    502: problem('Skins service unavailable'),
  },
};

export const tearsheetsIndexesGet: PathDef = {
  method: 'get',
  path: '/tearsheets/indexes',
  handler: 'domains/bff/src/tearsheets/indexes/index.handler',
  tags: ['tearsheets'],
  summary: 'Search Market Library non-traded indexes',
  description:
    'HFA getFilteredInstruments with TYPE_NONTRADED_INDEX. Returns InstrumentId + CommodityDescription for branding benchmark pickers. Requires a name query (HFA rejects an unfiltered list).',
  security: 'bearerAuth',
  query: TearsheetIndexQuery,
  responses: {
    200: json('Matching indexes', TearsheetIndexList),
    400: problem('Query too short or invalid'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    502: problem('HedgeFacts API unavailable'),
  },
};

export const tearsheetsLayoutComponentsGet: PathDef = {
  method: 'get',
  path: '/tearsheets/layout-components',
  handler: 'domains/bff/src/tearsheets/layout-components/list/index.handler',
  tags: ['tearsheets'],
  summary: 'List layout components for the signed-in datasource',
  security: 'bearerAuth',
  query: TearsheetLayoutComponentListQuery,
  responses: {
    200: json('Layout components', TearsheetLayoutComponentList),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    502: problem('Layout components service unavailable'),
  },
};

export const tearsheetsLayoutComponentPut: PathDef = {
  method: 'put',
  path: '/tearsheets/layout-components/{componentType}',
  handler: 'domains/bff/src/tearsheets/layout-components/put/index.handler',
  tags: ['tearsheets'],
  summary: 'Upsert a text layout component',
  security: 'bearerAuth',
  request: TearsheetLayoutComponentInput,
  responses: {
    200: json('Layout component', TearsheetLayoutComponent),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    502: problem('Layout components service unavailable'),
  },
};

export const tearsheetsLayoutComponentAssetPut: PathDef = {
  method: 'put',
  path: '/tearsheets/layout-components/{componentType}/asset',
  handler: 'domains/bff/src/tearsheets/layout-components/asset/index.handler',
  tags: ['tearsheets'],
  summary: 'Upload logo or portfolio manager photo',
  security: 'bearerAuth',
  request: TearsheetLayoutComponentAssetInput,
  responses: {
    200: json('Layout component', TearsheetLayoutComponent),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    413: problem('Asset too large'),
    502: problem('Layout components service unavailable'),
  },
};

export const tearsheetsLayoutComponentDelete: PathDef = {
  method: 'delete',
  path: '/tearsheets/layout-components/{componentType}',
  handler: 'domains/bff/src/tearsheets/layout-components/delete/index.handler',
  tags: ['tearsheets'],
  summary: 'Remove a layout component',
  security: 'bearerAuth',
  responses: {
    204: empty('Deleted'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    404: problem('Not found'),
    502: problem('Layout components service unavailable'),
  },
};

export const tearsheetsReportsPost: PathDef = {
  method: 'post',
  path: '/tearsheets/reports',
  handler: 'domains/bff/src/tearsheets/reports/post/index.handler',
  tags: ['tearsheets'],
  summary: 'Create a draft tearsheet',
  description:
    'Persists a draft for the signed-in datasource. Unique on fundId + asOf (month); skin comes from the layout. Duplicates return 409 with instance pointing at the existing report.',
  security: 'bearerAuth',
  request: TearsheetReportInput,
  responses: {
    201: json('Draft created', TearsheetReport),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    409: problem('A tearsheet already exists for this fund and month'),
    502: problem('Reports service unavailable'),
  },
};

export const tearsheetsReportsGet: PathDef = {
  method: 'get',
  path: '/tearsheets/reports',
  handler: 'domains/bff/src/tearsheets/reports/list/index.handler',
  tags: ['tearsheets'],
  summary: 'List tearsheets for a fund',
  description: 'Reports in the signed-in datasource for the given fundId, newest as-of first.',
  security: 'bearerAuth',
  query: TearsheetReportListQuery,
  responses: {
    200: json('Tearsheet reports', TearsheetReportList),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    502: problem('Reports service unavailable'),
  },
};

export const tearsheetsReportGet: PathDef = {
  method: 'get',
  path: '/tearsheets/reports/{reportId}',
  handler: 'domains/bff/src/tearsheets/reports/get/index.handler',
  tags: ['tearsheets'],
  summary: 'Get a tearsheet by id',
  description: 'Returns a report in the caller’s datasource.',
  security: 'bearerAuth',
  responses: {
    200: json('Tearsheet report', TearsheetReport),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    404: problem('Not found'),
    502: problem('Reports service unavailable'),
  },
};

export const tearsheetsReportPut: PathDef = {
  method: 'put',
  path: '/tearsheets/reports/{reportId}',
  handler: 'domains/bff/src/tearsheets/reports/put/index.handler',
  tags: ['tearsheets'],
  summary: 'Update tearsheet status or draft fields',
  description:
    'Status transitions follow TEARSHEET_REPORT_STATUS_TRANSITIONS. Reject = submitted/approved → archived. Unpublish = published → draft; re-open = archived → draft. Draft field updates (asOf/layoutId) are allowed only while status is draft. Do not mix status and field updates. A transition to submitted also starts the PDF render that the review steps hand out (fire-and-forget; it does not affect this response).',
  security: 'bearerAuth',
  request: TearsheetReportUpdate,
  responses: {
    200: json('Tearsheet report', TearsheetReport),
    400: problem('Invalid request or illegal status transition'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    404: problem('Not found'),
    409: problem('A tearsheet already exists for this fund and month'),
    502: problem('Reports service unavailable'),
  },
};

export const tearsheetsReportPdfGet: PathDef = {
  method: 'get',
  path: '/tearsheets/reports/{reportId}/pdf',
  handler: 'domains/bff/src/tearsheets/reports/pdf/index.handler',
  tags: ['tearsheets'],
  summary: 'Get tearsheet PDF export status / signed URL',
  description:
    'Returns `{ status, url?, detail? }` immediately. Optional query `pdfPageMode=continuous|a4` selects which stored paper to sign (default continuous). The first needed render produces both A4 and continuous PDFs; a draft rebuilds that pair on export, debounced so repeat exports within 5 minutes share it; every status after submitted serves the pair rendered when the report was submitted. `regenerate=true` ignores that stored pair and starts a new render (aggregator and owning companies) so a template or branding change shows up; send it only on the first poll. `successful` includes a short-lived S3 signed URL (Content-Disposition: inline, filename tearsheet-<fundName>-<asOf>.pdf). `pending` means Chromium render is in flight — poll this endpoint (~5s) until `successful` or `error`.',
  security: 'bearerAuth',
  query: TearsheetReportPdfQuery,
  responses: {
    200: json('PDF export status', TearsheetReportPdf),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    404: problem('Not found'),
    502: problem('PDF service unavailable'),
  },
};

export const tearsheetsPaths: PathDef[] = [
  tearsheetsDataGet,
  tearsheetsTermsGet,
  fundsGet,
  tearsheetsLayoutsGet,
  tearsheetsLayoutPut,
  tearsheetsLayoutsFavoritesPut,
  tearsheetsLayoutDelete,
  tearsheetsSkinsGet,
  tearsheetsSkinsPost,
  tearsheetsSkinsOrderPut,
  tearsheetsSkinPut,
  tearsheetsSkinDelete,
  tearsheetsIndexesGet,
  tearsheetsLayoutComponentsGet,
  tearsheetsLayoutComponentPut,
  tearsheetsLayoutComponentAssetPut,
  tearsheetsLayoutComponentDelete,
  tearsheetsReportsGet,
  tearsheetsReportsPost,
  tearsheetsReportGet,
  tearsheetsReportPut,
  tearsheetsReportPdfGet,
];
